import React from 'react';

import { Button } from 'components/base';
import LightFlipContractTerms from 'components/ContractTermsAndConditions/LightFlip';
import {LIGHT_FLIP_HANDLE} from 'constants/Shopify';
import {
  ContractPlanType,
  PhoneModelConfig,
  planPriceLabelShort
} from './types';

const DESCRIPTION =
  'This is a pre-order of the upcoming Light Flip, a minimal flip phone. You can cancel your pre-order at any point prior to shipping for a full refund. We’ll confirm your address prior to shipping.';

const DELIVERY_ESTIMATE = 'April 2027';

const lightFlipConfig: PhoneModelConfig = {
  key: 'light-flip',
  displayTitle: 'Light Flip',
  route: '/shop/products/light-flip',
  productHandle: LIGHT_FLIP_HANDLE,
  seo: {
    title: 'Light Flip',
    description: DESCRIPTION
  },
  colors: [
    {
      key: 'black',
      label: 'Black',
      swatchHex: '#1C1C1C',
      productKey: 'lightFlip',
      variantOptionValue: 'Black',
      backgroundStyle: { backgroundColor: '#1C1C1C', color: '#e6e6e6' },
      buttonColor: 'white',
      radioColor: 'gray',
      arrowColor: 'gray'
    },
    {
      key: 'navy',
      label: 'Navy',
      swatchHex: '#122140',
      productKey: 'lightFlip',
      variantOptionValue: 'Navy',
      backgroundStyle: { backgroundColor: '#122140', color: '#e6e6ef' },
      buttonColor: 'white',
      radioColor: 'gray',
      arrowColor: 'gray'
    },
    {
      key: 'red',
      label: 'Red',
      swatchHex: '#9f1b11',
      productKey: 'lightFlip',
      variantOptionValue: 'Red',
      backgroundStyle: { backgroundColor: '#9f1b11', color: '#f5e6e4' },
      buttonColor: 'white',
      radioColor: 'gray',
      arrowColor: 'gray'
    },
    {
      key: 'pink',
      label: 'Pink',
      swatchHex: '#fee4eb',
      productKey: 'lightFlip',
      variantOptionValue: 'Pink',
      backgroundStyle: { backgroundColor: '#fee4eb', color: '#141414' },
      buttonColor: 'black',
      radioColor: 'light',
      arrowColor: 'black'
    },
    {
      key: 'yellow',
      label: 'Yellow',
      swatchHex: '#fce572',
      productKey: 'lightFlip',
      variantOptionValue: 'Yellow',
      backgroundStyle: { backgroundColor: '#fce572', color: '#141414' },
      buttonColor: 'black',
      radioColor: 'light',
      arrowColor: 'black'
    },
    {
      key: 'gray',
      label: 'Gray',
      swatchHex: '#bdbdbd',
      productKey: 'lightFlip',
      variantOptionValue: 'Gray',
      backgroundStyle: { backgroundColor: '#bdbdbd', color: '#141414' },
      buttonColor: 'black',
      radioColor: 'light',
      arrowColor: 'black'
    }
  ],
  defaultColorKey: 'black',
  faqKey: 'lightFlipFAQ',
  showColorSelector: true,
  phoneStepButtonLabel: 'continue',
  deliveryEstimate: DELIVERY_ESTIMATE,
  contractPlans: [
    {
      type: ContractPlanType.LIMITED,
      variantMap: {
        Black: 'gid://shopify/ProductVariant/52821277638932',
        Gray: 'gid://shopify/ProductVariant/52821277671700',
        Navy: 'gid://shopify/ProductVariant/52821277704468',
        Red: 'gid://shopify/ProductVariant/52821277770004',
        Yellow: 'gid://shopify/ProductVariant/52821277802772',
        Pink: 'gid://shopify/ProductVariant/52821277737236'
      },
      monthlyPrice: 39,
      descriptionLabel: '1GB data + unlimited calls/texts'
    },
    {
      type: ContractPlanType.UNLIMITED,
      variantMap: {
        Black: 'gid://shopify/ProductVariant/52821317157140',
        Gray: 'gid://shopify/ProductVariant/52821317189908',
        Navy: 'gid://shopify/ProductVariant/52821317222676',
        Red: 'gid://shopify/ProductVariant/52821317288212',
        Yellow: 'gid://shopify/ProductVariant/52821317320980',
        Pink: 'gid://shopify/ProductVariant/52821317255444'
      },
      monthlyPrice: 69,
      descriptionLabel: 'Unlimited data/calls/texts'
    }
  ],
  purchaseOptionCopy: {
    contractLabel: planPriceLabelShort,
    contractSubheading: (
      <>
        with 2-year Light Service plan
        <br />
        (offer is limited to US customers)
      </>
    ),
    contractDescription: (deliveryDetails) => (
      <>
        <p>Estimated delivery for new pre-orders:</p>
        <p className="mb1">
          <span style={{ color: 'white', fontSize: '125%' }}>
            {deliveryDetails}
          </span>
        </p>
        <p className="mb1">
          Special pricing for the Light Flip when you sign up for a two-year
          service plan contract.
          <br />
          First month deposit due today to reserve the next available Light Flip.
        </p>
      </>
    ),
    fullPriceHeading: (priceFormatted) => `${priceFormatted} Unlocked (Pre-Order)`,
    fullPriceDescription: (
      <>
        <p>Estimated delivery for new pre-orders:</p>
        <p>
          <span style={{ color: 'white', fontSize: '125%' }}>
            {DELIVERY_ESTIMATE}
          </span>
        </p>
        <p className="mb2">
          <Button
            ariaLabel="more information about preorders"
            label="Learn more about pre-ordering."
            color="transparent-dust-gray-border"
            variant="underline"
            className="info-text-sm"
            to="/pre-order-details"
            font="akkurat"
          />
        </p>
        <p>
          This is a pre-order for the next available Light Flip, sold as a
          completely unlocked phone to use on the carrier of your choice.
        </p>
        <p className="mt3">
          NOTE: Outside the US, sales tax/VAT/customs or duty charges may be
          required upon delivery.
        </p>
        <p>
          <Button
            ariaLabel="open compatibility checker"
            label="Learn more about carrier compatibility."
            color="transparent-dust-gray-border"
            variant="underline"
            className="info-text-sm"
            to="/compatibility-checker"
            font="akkurat"
          />
        </p>
      </>
    )
  },
  isPlanPurchaseOptionEnabled: () => true,
  ContractTerms: LightFlipContractTerms
};

export default lightFlipConfig;
